/**
 * Team Analytics Service
 * 
 * @scalability Uses database function `get_team_leaderboard` for O(1) query 
 * complexity regardless of team size. Handles 1000+ team members efficiently.
 * 
 * Performance: Single RPC call replaces N+1 queries pattern.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/structured-logger';

const serviceLogger = logger.child({ service: 'TeamAnalyticsService' });

export interface TeamMember {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  post_count: number;
  total_engagement: number;
  avg_engagement: number;
  clicks: number;
  trials_attributed: number;
}

export interface TeamPerformance {
  total_posts: number;
  total_engagement: number;
  total_clicks: number;
  total_trials: number;
  active_members: number;
  avg_posts_per_member: number;
}

export const TeamAnalyticsService = {
  /**
   * Get team leaderboard using optimized database function
   * O(1) database call regardless of team size
   */
  async getTeamLeaderboard(organizationId: string, dateRange?: { start: string; end: string }): Promise<TeamMember[]> {
    try {
      serviceLogger.debug('Fetching team leaderboard', { organizationId, dateRange });

      const { data, error } = await supabase.rpc('get_team_leaderboard', {
        p_organization_id: organizationId,
        p_start_date: dateRange?.start || null,
        p_end_date: dateRange?.end || null
      });

      if (error) throw error;

      serviceLogger.info('Team leaderboard fetched', { 
        organizationId, 
        memberCount: data?.length || 0 
      });

      return (data || []).map(row => ({
        user_id: row.user_id,
        email: row.email,
        full_name: row.full_name,
        role: row.role,
        post_count: Number(row.post_count),
        total_engagement: Number(row.total_engagement),
        avg_engagement: Number(row.avg_engagement),
        clicks: Number(row.clicks),
        trials_attributed: Number(row.trials_attributed)
      }));
    } catch (error) {
      serviceLogger.error('Failed to fetch team leaderboard', error, { organizationId });
      throw error;
    }
  },

  /**
   * Get aggregate team performance
   * Derives from leaderboard data for consistency
   */
  async getTeamPerformance(organizationId: string, dateRange?: { start: string; end: string }): Promise<TeamPerformance> {
    try {
      const leaderboard = await this.getTeamLeaderboard(organizationId, dateRange);

      const totalPosts = leaderboard.reduce((sum, m) => sum + m.post_count, 0);
      const totalEngagement = leaderboard.reduce((sum, m) => sum + m.total_engagement, 0);
      const totalClicks = leaderboard.reduce((sum, m) => sum + m.clicks, 0);
      const totalTrials = leaderboard.reduce((sum, m) => sum + m.trials_attributed, 0);
      const activeMembers = leaderboard.filter(m => m.post_count > 0).length;

      return {
        total_posts: totalPosts,
        total_engagement: totalEngagement,
        total_clicks: totalClicks,
        total_trials: totalTrials,
        active_members: activeMembers,
        avg_posts_per_member: activeMembers > 0 ? Math.round(totalPosts / activeMembers) : 0
      };
    } catch (error) {
      serviceLogger.error('Failed to fetch team performance', error, { organizationId });
      throw error;
    }
  },

  /**
   * Get individual member performance
   */
  async getMemberPerformance(userId: string, organizationId: string, dateRange?: { start: string; end: string }): Promise<TeamMember | null> {
    try {
      const leaderboard = await this.getTeamLeaderboard(organizationId, dateRange);
      return leaderboard.find(m => m.user_id === userId) || null;
    } catch (error) {
      serviceLogger.error('Failed to fetch member performance', error, { userId, organizationId });
      throw error;
    }
  }
};
